import {
    ChevronDown,
    Copy,
    Download,
    Save,
    Search,
    Share2,
    SlidersHorizontal,
    Trash2,
} from 'lucide-react';

import { Button } from 'components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'components/ui/select';

import type { ReportDateRangeValue } from './api/fetchReportData';
import type { ReportExportFormat } from './exportReportData';
import { ReportDateRangePicker } from './ReportDateRangePicker';
import { TicketFilterDropdown } from './TicketFilterDropdown';
import type { TicketOption } from './ticketFilter';

type ReportToolbarProps = {
    searchValue: string;
    onSearchChange: (value: string) => void;
    dateRange: ReportDateRangeValue;
    onDateRangeChange: (value: ReportDateRangeValue) => void;
    eventName: string;
    showEventField?: boolean;
    ticketOptions?: TicketOption[];
    selectedTicketIds?: number[];
    onTicketIdsChange?: (ticketIds: number[] | undefined) => void;
    showTicketFilter?: boolean;
    onCustomizeColumns: () => void;
    onExport: (format: ReportExportFormat) => void;
    exportDisabled?: boolean;
    onSaveView?: () => void;
    onShareView?: () => void;
    onDeleteView?: () => void;
    onCloneView?: () => void;
    saveLabel?: string;
    canShareView?: boolean;
    canDeleteView?: boolean;
    canCloneView?: boolean;
    saveDisabled?: boolean;
    deleteDisabled?: boolean;
    cloneDisabled?: boolean;
};

export function ReportToolbar({
    searchValue,
    onSearchChange,
    dateRange,
    onDateRangeChange,
    eventName,
    showEventField = false,
    ticketOptions = [],
    selectedTicketIds,
    onTicketIdsChange,
    showTicketFilter = false,
    onCustomizeColumns,
    onExport,
    exportDisabled = false,
    onSaveView,
    onShareView,
    onDeleteView,
    onCloneView,
    saveLabel = 'Save view',
    canShareView = false,
    canDeleteView = false,
    canCloneView = false,
    saveDisabled = false,
    deleteDisabled = false,
    cloneDisabled = false,
}: ReportToolbarProps) {
    return (
        <div className="flex flex-col gap-4">
            <div
                className={
                    showTicketFilter || showEventField
                        ? 'grid gap-4 md:grid-cols-4 md:items-end'
                        : 'grid gap-4 md:grid-cols-3 md:items-end'
                }
            >
                <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Date Range
                    </Label>
                    <ReportDateRangePicker value={dateRange} onChange={onDateRangeChange} />
                </div>

                {showTicketFilter ? (
                    <TicketFilterDropdown
                        ticketOptions={ticketOptions}
                        selectedTicketIds={selectedTicketIds}
                        onChange={onTicketIdsChange ?? (() => undefined)}
                    />
                ) : null}

                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={searchValue}
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder="Search report..."
                        className="pl-9"
                    />
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                    {/* Shared views: Clone. Owned / base report: Save. Never both. */}
                    {canCloneView && onCloneView ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onCloneView}
                            disabled={cloneDisabled}
                        >
                            <Copy className="h-4 w-4" />
                            Clone
                        </Button>
                    ) : onSaveView ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onSaveView}
                            disabled={saveDisabled}
                        >
                            <Save className="h-4 w-4" />
                            {saveLabel}
                        </Button>
                    ) : null}
                    {canShareView && onShareView ? (
                        <Button variant="outline" size="sm" onClick={onShareView}>
                            <Share2 className="h-4 w-4" />
                            Share
                        </Button>
                    ) : null}
                    {/* Owner-only; API also returns 403 for non-owners */}
                    {canDeleteView && onDeleteView ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onDeleteView}
                            disabled={deleteDisabled}
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                            <Trash2 className="h-4 w-4" />
                            Delete
                        </Button>
                    ) : null}
                    <Button variant="outline" size="sm" onClick={onCustomizeColumns}>
                        <SlidersHorizontal className="h-4 w-4" />
                        Fields
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button size="sm" disabled={exportDisabled}>
                                <Download className="h-4 w-4" />
                                Export
                                <ChevronDown className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="reports-light-overlay">
                            <DropdownMenuItem onClick={() => onExport('csv')}>CSV</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onExport('xlsx')}>
                                Excel
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onExport('pdf')}>PDF</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {showEventField ? (
                    <div className="space-y-2">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Event
                        </Label>
                        <Select value="current-event">
                            <SelectTrigger>
                                <SelectValue placeholder={eventName} />
                            </SelectTrigger>
                            <SelectContent className="reports-light-overlay">
                                <SelectItem value="current-event">{eventName}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
